/**
 * "Agendar <recurso>" (spec 039): o drawer do compromisso com o recurso
 * PRIMEIRO, no padrão do commitment-drawer e em outra ordem —
 *
 * 1. a busca do ticket: o casamento da lista de tickets (spec 012), que acha
 *    a placa em qualquer grafia, pela leitura própria da agenda
 *    (`GET /agenda/tickets`); abertos primeiro. Escolher o ticket define o
 *    cliente. Nada achado, a tela diz — não se cria ticket por aqui. Com o
 *    switch "Mostrar clientes ocultos" da aba (adendo), acha também os tickets
 *    ocultos, com a marca "Oculto no ERP";
 * 2. data — um dia só (data final = inicial) — e "Horário" | "Período"
 *    (adendo 3): "Horário" mostra início e fim, 08:00–09:00; "Período" mostra
 *    Manhã | Tarde e esconde os horários (o servidor grava a janela). O estado
 *    e o corpo saem de `plate-schedule.ts`, módulo puro que a guarda da API
 *    prova;
 * 3. atendente: quem está logado, ou qualquer pessoa ativa da equipe;
 * 4. observação, opcional.
 *
 * Também abre com o ticket já escolhido (o atendimento, e a lista "Aguardando
 * data" do adendo 2, que passa também o período que o cliente escolheu no bot:
 * com ele, abre em "Período") e para reagendar um compromisso de um dia de uma
 * pessoa, mostrando o tipo gravado. O "Novo compromisso" não muda: é só
 * horário.
 *
 * O nome do recurso ("placa") vem do perfil do tenant: a guarda de
 * vocabulário reprova a palavra escrita em tela.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  COMMITMENT_PERIODS,
  COMMITMENT_PERIOD_LABELS,
  capitalize,
  saoPauloDay,
  type AgendaAwaitingItem,
  type AgendaTicketSearchItem,
  type Commitment,
  type CommitmentBody,
  type CommitmentPeriod,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge, Dialog, Spinner } from '@/components/ui/misc';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { teamListQuery } from '@/features/team/team-queries';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { agendaTicketsQuery, errorMessage } from './agenda-queries';
import { useCreateCommitment, useUpdateCommitment } from './agenda-mutations';
import { HiddenTicketBadge } from './hidden-ticket-mark';
import {
  initialPlateSchedule,
  plateScheduleBody,
  plateScheduleValid,
  type PlateSchedule,
  type PlateScheduleKind,
} from './plate-schedule';

/** O ticket escolhido, do jeito que o drawer o mostra. */
export interface PlateTicketRef {
  ticketNumber: number;
  title: string;
  /** o rótulo de recurso do ticket (perfil do tenant), ou null */
  plate: string | null;
  clientId: number;
  clientName: string;
  deskName: string | null;
  stageName: string | null;
  /** oculto pela ocultação do tenant (adendo) — a API decide, a tela marca */
  hidden?: boolean;
}

export function plateTicketOfCommitment(c: Commitment): PlateTicketRef {
  return {
    ticketNumber: c.ticketNumber,
    title: c.ticketTitle,
    plate: c.ticketPlate ?? null,
    clientId: c.clientId,
    clientName: c.clientName,
    deskName: c.ticketDeskName ?? null,
    stageName: c.ticketStageName ?? null,
    hidden: c.ticketHidden,
  };
}

function plateTicketOfSearchItem(t: AgendaTicketSearchItem): PlateTicketRef {
  return {
    ticketNumber: t.ticketNumber,
    title: t.title,
    plate: t.resourceLabel,
    clientId: t.clientId,
    clientName: t.clientName,
    deskName: t.deskName,
    stageName: t.stageName,
    hidden: t.hidden,
  };
}

/** O ticket de uma linha da lista "Aguardando data" (adendo 2), com a marca de oculto. */
export function plateTicketOfAwaitingItem(t: AgendaAwaitingItem): PlateTicketRef {
  return {
    ticketNumber: t.ticketNumber,
    title: t.title,
    plate: t.resourceLabel,
    clientId: t.clientId,
    clientName: t.clientName,
    deskName: t.deskName,
    stageName: t.stageName,
    hidden: t.hidden,
  };
}

/** a busca começa com 2 caracteres — 1 só casaria meia base */
const MIN_SEARCH = 2;

const KIND_OPTIONS: ReadonlyArray<{ value: PlateScheduleKind; label: string }> = [
  { value: 'time', label: 'Horário' },
  { value: 'period', label: 'Período' },
];
const PERIOD_OPTIONS = COMMITMENT_PERIODS.map((p) => ({ value: p, label: COMMITMENT_PERIOD_LABELS[p] }));

/** O seletor segmentado das abas da agenda (botões com `aria-pressed`), dentro do formulário. */
function Segmented<T extends string>({
  labelledBy,
  options,
  value,
  onChange,
}: {
  labelledBy: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      className="flex h-9 w-fit items-center rounded-md border bg-card p-0.5"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'h-8 cursor-pointer rounded-[5px] px-3 text-sm font-medium transition-colors',
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function PlateCommitmentDrawer({
  initial,
  ticket,
  botPeriod,
  includeHidden = false,
  onClose,
}: {
  /** reagendar (Editar) — um dia, uma pessoa (`editsInPlateDrawer`), no tipo gravado */
  initial?: Commitment;
  /** abrir com o ticket já escolhido */
  ticket?: PlateTicketRef;
  /** o período do bot (lista "Aguardando data"): abre em "Período" com ele; sem, "Horário" 08:00–09:00 */
  botPeriod?: CommitmentPeriod | null;
  /** o switch "Mostrar clientes ocultos" da aba (adendo): a busca acha os ocultos */
  includeHidden?: boolean;
  onClose: () => void;
}) {
  const terms = useTerms();
  const me = useAuthUser();
  const [picked, setPicked] = useState<PlateTicketRef | null>(
    initial ? plateTicketOfCommitment(initial) : (ticket ?? null)
  );
  const [searchText, setSearchText] = useState('');
  const debounced = useDebouncedValue(searchText, 300);
  const term = debounced.trim();

  const [date, setDate] = useState(initial?.startDate ?? saoPauloDay(new Date()));
  // adendo 3: "Horário" | "Período" — os dois lados guardados, trocar não perde nada
  const [schedule, setSchedule] = useState<PlateSchedule>(() => initialPlateSchedule({ initial, botPeriod }));
  // quem está logado por padrão (no SSO, id 0 até o /me responder: sem padrão)
  const [assignee, setAssignee] = useState(
    String(initial?.assigneeUserId ?? (me && me.id > 0 ? me.id : ''))
  );
  const [observation, setObservation] = useState(initial?.description ?? '');

  const ticketsQ = useQuery({
    ...agendaTicketsQuery({ search: term, includeHidden }),
    enabled: picked === null && term.length >= MIN_SEARCH,
  });
  // a equipe ativa deste tenant (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery());
  const activeUsers = usersQ.data?.items ?? [];
  // reagendar: quem já estava gravado continua escolhível, mesmo fora da lista
  const storedMissing =
    initial?.assigneeUserId != null && !activeUsers.some((u) => u.id === initial.assigneeUserId);
  const assigneeKnown =
    activeUsers.some((u) => String(u.id) === assignee) ||
    (storedMissing && String(initial?.assigneeUserId) === assignee);

  const create = useCreateCommitment();
  const update = useUpdateCommitment(initial?.id ?? 0);
  const mutation = initial ? update : create;

  // abertos primeiro e, dentro de cada grupo, os mais recentes: a ordem do servidor
  const results = ticketsQ.data ?? [];

  const scheduleOk = plateScheduleValid(schedule);
  const valid = picked !== null && date !== '' && scheduleOk && assigneeKnown;
  const idLabel = capitalize(terms.resourceId);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || picked === null) return;
    const body: CommitmentBody = {
      clientId: picked.clientId,
      ticketNumber: picked.ticketNumber,
      assigneeUserId: Number(assignee),
      calendarId: null,
      startDate: date,
      endDate: date,
      ...plateScheduleBody(schedule),
      // o lembrete não muda nesta spec: o padrão do "Novo compromisso"
      reminderMinutes: initial?.reminderMinutes ?? 30,
      description: observation.trim(),
    };
    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? 'Reagendar' : `Agendar ${terms.resourceId}`}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="plate-search">
            {idLabel} <span className="text-destructive">*</span>
          </Label>
          {picked ? (
            <div className="flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm contain-[inline-size]">
              <div className="min-w-0">
                <div className="truncate">
                  <span className="font-semibold">{picked.plate ?? picked.title}</span>{' '}
                  <span className="text-muted-foreground">#{picked.ticketNumber}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {[picked.clientName, picked.deskName, picked.stageName].filter(Boolean).join(' · ')}
                </div>
                {picked.hidden ? (
                  <div className="mt-1">
                    <HiddenTicketBadge />
                  </div>
                ) : null}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPicked(null)}>
                Alterar
              </Button>
            </div>
          ) : (
            <div className="space-y-2 contain-[inline-size]">
              <Input
                id="plate-search"
                autoFocus
                placeholder={`Buscar por ${terms.resourceId} ou nº do ticket…`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {term.length >= MIN_SEARCH ? (
                <div className="max-h-56 overflow-y-auto rounded-md border">
                  {ticketsQ.isPending ? (
                    <div className="flex justify-center p-3">
                      <Spinner />
                    </div>
                  ) : ticketsQ.isError ? (
                    <p className="p-3 text-sm text-destructive">{errorMessage(ticketsQ.error)}</p>
                  ) : results.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Nenhum ticket encontrado para “{term}”. O agendamento é de um ticket que já
                      existe.
                    </p>
                  ) : (
                    <ul className="divide-y divide-divider">
                      {results.map((t) => (
                        <li key={t.ticketNumber}>
                          <button
                            type="button"
                            onClick={() => setPicked(plateTicketOfSearchItem(t))}
                            className="flex w-full min-w-0 cursor-pointer items-start gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate">
                                <span className="text-muted-foreground">#{t.ticketNumber}</span>{' '}
                                <span className="font-medium">{t.resourceLabel ?? t.title}</span>
                              </div>
                              <div className="truncate text-xs text-muted-foreground">
                                {[t.clientName, t.deskName, t.stageName].join(' · ')}
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              {t.hidden ? <HiddenTicketBadge /> : null}
                              {t.state === 'closed' ? (
                                <Badge>Fechado</Badge>
                              ) : t.state === 'canceled' ? (
                                <Badge>Cancelado</Badge>
                              ) : null}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label id="plate-kind-label">
            Agendar por <span className="text-destructive">*</span>
          </Label>
          <Segmented
            labelledBy="plate-kind-label"
            options={KIND_OPTIONS}
            value={schedule.kind}
            onChange={(kind) => setSchedule((s) => ({ ...s, kind }))}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="plate-date">
              Data <span className="text-destructive">*</span>
            </Label>
            <Input
              id="plate-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          {schedule.kind === 'time' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="plate-start">
                  Início <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="plate-start"
                  type="time"
                  value={schedule.startTime}
                  onChange={(e) => setSchedule((s) => ({ ...s, startTime: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="plate-end">
                  Fim <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="plate-end"
                  type="time"
                  value={schedule.endTime}
                  onChange={(e) => setSchedule((s) => ({ ...s, endTime: e.target.value }))}
                  required
                />
              </div>
            </>
          ) : (
            <div className="space-y-1.5 sm:col-span-2">
              <Label id="plate-period-label">
                Período <span className="text-destructive">*</span>
              </Label>
              <Segmented
                labelledBy="plate-period-label"
                options={PERIOD_OPTIONS}
                value={schedule.period}
                onChange={(period) => setSchedule((s) => ({ ...s, period }))}
              />
            </div>
          )}
        </div>
        {schedule.kind === 'time' && schedule.startTime && schedule.endTime && !scheduleOk ? (
          <p className="-mt-2 text-xs text-destructive">O fim deve ser depois do início.</p>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="plate-assignee">
            Atendente <span className="text-destructive">*</span>
          </Label>
          <Select
            id="plate-assignee"
            value={assigneeKnown ? assignee : ''}
            onChange={(e) => setAssignee(e.target.value)}
            disabled={usersQ.isPending}
            required
          >
            <option value="" disabled>
              {usersQ.isPending ? 'Carregando equipe…' : 'Selecione o atendente…'}
            </option>
            {storedMissing && initial?.assigneeUserId != null ? (
              <option value={initial.assigneeUserId}>{initial.assigneeName ?? `#${initial.assigneeUserId}`}</option>
            ) : null}
            {activeUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="plate-observation">Observação</Label>
          <Textarea
            id="plate-observation"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder="Opcional"
          />
        </div>

        {mutation.error ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Agendar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
